import React, { useState, useEffect } from 'react';
import { PlayIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8005';

export default function Messages() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (user) {
      fetchMessages();
    }
  }, [user, filter]);

  const fetchMessages = async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/recordings/user/${user.id}`, {
        params: {
          category: filter !== 'all' ? filter : undefined
        }
      });
      setMessages(response.data.recordings || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/recordings/${messageId}`);
      setMessages(messages.filter(m => m._id !== messageId));
    } catch (error) {
      console.error('Failed to delete message:', error);
      alert('Failed to delete message');
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">Your Messages</h1>
            <div className="flex space-x-2">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="all">All Categories</option>
                <option value="advice">Advice</option>
                <option value="memory">Memory</option>
                <option value="love_letter">Love Letter</option>
                <option value="birthday">Birthday</option>
                <option value="wedding">Wedding</option>
                <option value="graduation">Graduation</option>
              </select>
            </div>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="text-gray-600 mt-2">Loading messages...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-gray-500">No messages found</p>
              <a
                href="/record"
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                Record Your First Message
              </a>
            </div>
          ) : (
            messages.map((message) => (
              <div key={message._id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-lg font-medium text-gray-900">
                        {message.title}
                      </h3>
                      {message.category && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                          {message.category}
                        </span>
                      )}
                      {message.is_draft && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Draft
                        </span>
                      )}
                    </div>
                    
                    <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                      {message.duration && (
                        <span>{formatDuration(message.duration)}</span>
                      )}
                      {message.occasion && (
                        <span>• {message.occasion}</span>
                      )}
                      <span>• {formatDate(message.created_at)}</span>
                    </div>

                    {message.tags && message.tags.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {message.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {message.transcription && (
                      <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                        {message.transcription}
                      </p>
                    )}
                  </div>

                  <div className="ml-4 flex items-center space-x-2">
                    <button
                      onClick={() => window.open(message.audio_file_url, '_blank')}
                      className="p-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-full"
                      title="Play"
                    >
                      <PlayIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => window.location.href = `/record?edit=${message._id}`}
                      className="p-2 text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-full"
                      title="Edit"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(message._id)}
                      className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full"
                      title="Delete"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
